import React from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  Checkbox,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@weave-mui/material';
import Button, { buttonVariants } from '@weave-mui/button';
import {
  Search as SearchIcon,
  StorageOutlined as StorageIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';
import {
  EditM as EditIcon,
  TrashM as TrashIcon,
  CopyM as CopyIcon,
  ExportMoveM as MoveIcon,
} from '@weave-mui/icons-weave';
import TotalSelectedBadge from '../../components/TotalSelectedBadge';
import { useSelector } from 'react-redux';
import HostingEnvironmentSwitch from 'components/EnvironmentSwitch/HostingEnvironmentSwitch';

const HostingControls = ({
  selectedAll,
  onSelectAll,
  available,
  selected,
  total,
  showModalWithContent,
  onChangeView,
  viewType,
  onSearchInputChange,
  searchValue,
  environments,
  context,
  projects,
  onManageTestData,
}) => {
  const role = useSelector((state) => state.hosting.role);

  const iconButtonStyles = {
    color: 'text.primary',
    padding: '4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  };

  return (
    <>
      {/* Top row: Search bar, View toggle, Add test on left; Environment dropdown and Submit tests on right */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {/* Left side: Search bar, View toggle, Add test button */}
        <Box sx={{ display: 'flex', alignItems: 'normal', gap: 2 }}>
          {/* Search bar */}
          <TextField
            variant="filled"
            placeholder="Search test cases by ..."
            value={searchValue}
            onChange={onSearchInputChange}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 446,
              '& .MuiFilledInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                border: 'none !important',
                borderRadius: '4px',
                paddingLeft: '8px',
                '&:before': {
                  display: 'none !important',
                },
                '&:after': {
                  display: 'none !important',
                },
              },
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                border: 'none !important',
                height: '36px',
                '&:before': {
                  display: 'none !important',
                },
                '&:after': {
                  display: 'none !important',
                },
              },
              '& .MuiInputBase-input': {
                padding: '9px 0 9px !important',
                fontSize: '16px',
                lineHeight: '20px',
                color: 'rgba(0, 0, 0, 0.6)',
                backgroundColor: 'transparent !important',
              },
              '& .MuiInputAdornment-root': {
                marginTop: '0px !important',
                marginRight: '4px',
              },
            }}
          />

          {/* View Toggle */}
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={(_, newView) => {
              if (newView !== null) {
                onChangeView(newView);
              }
            }}
            aria-label="view type"
            size="small"
            sx={{
              height: '32px',
              '& .MuiToggleButton-root': {
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                padding: '4px',
                marginRight: '4px',
                '&:last-child': {
                  marginRight: 0,
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  },
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="table" aria-label="list view">
              <ListViewIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Add a test button - always visible for admin */}
          {role === 'admin' && (
            <Button
              variant={buttonVariants.CONTAINED}
              onClick={showModalWithContent('newTestCase')}
              disabled={projects.length === 0}
              color="primary"
            >
              Add a test
            </Button>
          )}
        </Box>

        {/* Right side: Environment dropdown and Submit tests button */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {environments?.length > 0 && (
            <HostingEnvironmentSwitch
              environments={environments}
              context={context}
            />
          )}

          {/* Submit tests button - always visible, disabled when no selection */}
          <Button
            variant={buttonVariants.CONTAINED}
            onClick={showModalWithContent('submitTestCases')}
            disabled={selected === 0}
            color="primary"
          >
            Submit tests
          </Button>
        </Box>
      </Box>

      {/* Second row: Select All checkbox and action icons on left, TotalSelectedBadge on right */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {/* Left side: Select All checkbox and action icon buttons */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Box
            component="label"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
          >
            <Checkbox onChange={onSelectAll} checked={selectedAll} />
            Select all
          </Box>

          {/* Action icon buttons - shown when test cases are selected */}
          {selected > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={showModalWithContent('editTestCasesBulk')}
                  size="small"
                  sx={iconButtonStyles}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={showModalWithContent('deleteTestCasesBulk')}
                  size="small"
                  sx={iconButtonStyles}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Clone">
                <IconButton
                  onClick={showModalWithContent('cloneTestCasesBulk')}
                  size="small"
                  sx={iconButtonStyles}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Move">
                <IconButton
                  onClick={showModalWithContent('moveTestCasesBulk')}
                  size="small"
                  sx={iconButtonStyles}
                >
                  <MoveIcon />
                </IconButton>
              </Tooltip>

              {role === 'admin' && (
                <Tooltip title="Manage Test Data">
                  <IconButton
                    onClick={
                      onManageTestData
                        ? onManageTestData
                        : showModalWithContent('addTestData')
                    }
                    size="small"
                    sx={iconButtonStyles}
                  >
                    <StorageIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Right side: TotalSelectedBadge */}
        <TotalSelectedBadge
          available={available}
          selected={selected}
          total={total}
        />
      </Box>
    </>
  );
};

export default HostingControls;
